import { Alert, Box } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { externalIdentityQuery } from "../../api/externalIdentity";

/**
 * A calm, page-level notice for an external provider whose account cannot yet reach patient data.
 *
 * Pending approval is the normal state of every newly-invited provider, so the gated endpoints used
 * to answer with a 403 that the global axios interceptor turned into an error dialog — a popup, on
 * whatever page they happened to open. This replaces that with one banner rendered once for the
 * external audience (see RoutedContent), so the same statement appears at the top, everywhere. The
 * matching 403s now carry suppressErrorDialog so the popup no longer fires (see ExternalGateResponse).
 *
 * The banner reads the provider's OWN identity (/external/identity is not behind the approval gate),
 * so it works even while every patient-data request is being refused. It shares that read with the
 * rest of the external portal (see src/api/externalIdentity.ts) rather than fetching its own copy,
 * so the banner and the page underneath it can never disagree about the account's state.
 */
const ExternalAccountBanner = () => {
  const { data: provider, isError } = useQuery(externalIdentityQuery);

  // Approval status could not be determined. Say nothing rather than assert a state we do not
  // know — the server still enforces access regardless of what the banner shows.
  if (isError || !provider) return null;

  // Delegated office staff: a calm, always-on reminder that actions are recorded under their own
  // name and taken on their providers' behalf. This is a persistent, non-error notice, so keep its
  // severity distinct from the provider account-state messages below.
  if (provider.isDelegate) {
    return (
      <Banner>
        You&apos;re working on behalf of{" "}
        {provider.externalOrganizationName ?? "your practice"}. Everything you do
        is recorded under your name.
      </Banner>
    );
  }

  // Deactivated is the more serious state, so it takes precedence over pending approval.
  if (provider.isActive === false) {
    return (
      <Banner>
        Your external provider account has been deactivated. Contact the clinic
        if you believe this is a mistake.
      </Banner>
    );
  }

  if (provider.isApproved === false) {
    return (
      <Banner>
        Your account is pending approval by the clinic. Once an administrator
        approves your access, your patients will appear here.
      </Banner>
    );
  }

  return null;
};

const Banner = ({ children }: { children: React.ReactNode }) => (
  <Box sx={{ px: { xs: 2, sm: 3 }, pt: 2 }}>
    <Alert severity="info">{children}</Alert>
  </Box>
);

export default ExternalAccountBanner;
